import { useState, useEffect } from 'react';
import { getUserId } from '../../../../services/session.js';
import Drawer from '../../../../components/v2/Drawer.jsx';

/**
 * EnvironmentalDrawer — Add/Edit environmental control settings.
 *
 * Adapts form fields based on envType:
 *   - temperature: Low/High Temp (°F), range -30–120
 *   - humidity:    Low/High Humidity (%), range 20–90
 *   - battery:     Low Battery (V), range 2.0–4.0, no high threshold
 *
 * Props:
 *   open       — drawer visibility
 *   onClose    — close callback
 *   onSave     — save callback (receives formData object)
 *   saving     — boolean, disables buttons while saving
 *   editTarget — existing record to edit, or null for add mode
 *   envType    — 'temperature' | 'humidity' | 'battery'
 *   campusId   — current campus ID
 */

const ENV_TYPE_MAP = { temperature: 1, humidity: 2, battery: 3 };

const FIELD_CONFIG = {
    temperature: {
        lowLabel: 'Low Temp (°F)',
        highLabel: 'High Temp (°F)',
        lowMin: -30, lowMax: 120, lowStep: 1,
        highMin: -30, highMax: 120, highStep: 1,
        hasHigh: true,
        defaultLow: 50, defaultHigh: 80,
    },
    humidity: {
        lowLabel: 'Low Humidity (%)',
        highLabel: 'High Humidity (%)',
        lowMin: 20, lowMax: 90, lowStep: 1,
        highMin: 20, highMax: 90, highStep: 1,
        hasHigh: true,
        defaultLow: 50, defaultHigh: 80,
    },
    battery: {
        lowLabel: 'Low Battery (V)',
        lowMin: 2.0, lowMax: 4.0, lowStep: 0.1,
        hasHigh: false,
        defaultLow: 2.4,
    },
};

const descriptionPattern = /^[a-zA-Z0-9_ \-/]+$/;

const EnvironmentalDrawer = ({ open, onClose, onSave, saving, editTarget, envType, campusId }) => {
    const isEdit = !!editTarget;
    const cfg = FIELD_CONFIG[envType] || FIELD_CONFIG.temperature;
    const isCustom = !editTarget || editTarget.environmental_alarm_type_id === 4;

    const [description, setDescription] = useState('');
    const [lowAlarm, setLowAlarm] = useState(cfg.defaultLow);
    const [highAlarm, setHighAlarm] = useState(cfg.defaultHigh || 0);
    const [alarmOn, setAlarmOn] = useState(true);
    const [interval, setInterval] = useState(60);
    const [errors, setErrors] = useState({});

    // Initialize form when drawer opens
    useEffect(() => {
        if (open) {
            if (editTarget) {
                setDescription(editTarget.description || '');
                setLowAlarm(editTarget.low_alarm ?? cfg.defaultLow);
                setHighAlarm(editTarget.high_alarm ?? cfg.defaultHigh ?? 0);
                setAlarmOn(editTarget.alarm_on === 1);
                setInterval(editTarget.test_after && editTarget.test_after >= 60
                    ? Math.round(editTarget.test_after / 60)
                    : 60);
            } else {
                setDescription('');
                setLowAlarm(cfg.defaultLow);
                setHighAlarm(cfg.defaultHigh || 0);
                setAlarmOn(true);
                setInterval(60);
            }
            setErrors({});
        }
    }, [open, editTarget, envType, cfg.defaultLow, cfg.defaultHigh]);

    const clearError = (field) => {
        setErrors((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const validate = () => {
        const errs = {};

        if (isCustom) {
            if (!description.trim()) {
                errs.description = 'Description is required.';
            } else if (!descriptionPattern.test(description.trim())) {
                errs.description = 'Only letters, numbers, spaces, and - _ / are allowed.';
            }
        }

        if (lowAlarm === '' || lowAlarm == null) {
            errs.lowAlarm = 'Low threshold is required.';
        }

        if (cfg.hasHigh && (highAlarm === '' || highAlarm == null)) {
            errs.highAlarm = 'High threshold is required.';
        }

        if (interval === '' || interval == null || parseInt(interval) < 30) {
            errs.interval = 'Minimum 30 minutes.';
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;

        const formData = {
            description: isCustom ? description.trim() : editTarget.description,
            low_alarm: envType === 'battery' ? parseFloat(lowAlarm) : parseInt(lowAlarm),
            alarm_on: alarmOn ? 1 : 0,
            test_after: parseInt(interval) * 60,
            campus_id: campusId,
            environmental_type_id: ENV_TYPE_MAP[envType],
            modified_by: getUserId(),
        };

        if (cfg.hasHigh) {
            formData.high_alarm = parseInt(highAlarm);
        }

        if (isEdit) {
            formData.control_id = editTarget.control_id;
            formData.environmental_alarm_type_id = editTarget.environmental_alarm_type_id;
            formData.device_ids = editTarget.device_ids || '';
        } else {
            formData.environmental_alarm_type_id = 4; // Custom
            formData.device_ids = '';
        }

        onSave(formData);
    };

    const title = isEdit
        ? `Edit ${envType === 'temperature' ? 'Temperature' : envType === 'humidity' ? 'Humidity' : 'Battery'} Setting`
        : `Add ${envType === 'temperature' ? 'Temperature' : envType === 'humidity' ? 'Humidity' : 'Battery'} Setting`;

    return (
        <>
            <Drawer
                open={open}
                onClose={onClose}
                title={title}
                footer={(
                    <>
                        <button className="dp-btn dp-btn--cancel" onClick={onClose}>Cancel</button>
                        <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleSave}>
                            {saving ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Save Changes' : 'Add Setting')}
                        </button>
                    </>
                )}
            >
                {/* Description */}
                <div className="dp-drawer__field">
                    <label className="dp-drawer__label">
                        Description {isCustom ? <span className="dp-drawer__required">*</span> : null}
                    </label>
                    <input
                        className={`dp-drawer__input${errors.description ? ' is-invalid' : ''}`}
                        type="text"
                        value={description}
                        onChange={(e) => { setDescription(e.target.value); clearError('description'); }}
                        readOnly={!isCustom}
                        style={!isCustom ? { backgroundColor: 'var(--color-bg-tertiary, #f3f4f6)', cursor: 'not-allowed' } : undefined}
                    />
                    {errors.description ? <p className="dp-drawer__error">{errors.description}</p> : null}
                    {!isCustom ? (
                        <p style={{ marginTop: 'var(--space-1, 0.25rem)', fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--color-text-muted, #6b7280)' }}>
                            Default record descriptions cannot be changed.
                        </p>
                    ) : null}
                </div>

                {/* Low Threshold */}
                <div className="dp-drawer__field">
                    <label className="dp-drawer__label">
                        {cfg.lowLabel} <span className="dp-drawer__required">*</span>
                    </label>
                    <input
                        className={`dp-drawer__input${errors.lowAlarm ? ' is-invalid' : ''}`}
                        type="number"
                        min={cfg.lowMin}
                        max={cfg.lowMax}
                        step={cfg.lowStep}
                        value={lowAlarm}
                        onChange={(e) => { setLowAlarm(e.target.value); clearError('lowAlarm'); }}
                    />
                    {errors.lowAlarm ? <p className="dp-drawer__error">{errors.lowAlarm}</p> : null}
                </div>

                {/* High Threshold (Temperature/Humidity only) */}
                {cfg.hasHigh ? (
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">
                            {cfg.highLabel} <span className="dp-drawer__required">*</span>
                        </label>
                        <input
                            className={`dp-drawer__input${errors.highAlarm ? ' is-invalid' : ''}`}
                            type="number"
                            min={cfg.highMin}
                            max={cfg.highMax}
                            step={cfg.highStep}
                            value={highAlarm}
                            onChange={(e) => { setHighAlarm(e.target.value); clearError('highAlarm'); }}
                        />
                        {errors.highAlarm ? <p className="dp-drawer__error">{errors.highAlarm}</p> : null}
                    </div>
                ) : null}

                {/* Alarm On Toggle */}
                <div className="ws-toggle-row">
                    <span className="ws-toggle-label">Generate Alarms</span>
                    <label className="dp-toggle">
                        <input
                            type="checkbox"
                            checked={alarmOn}
                            onChange={(e) => setAlarmOn(e.target.checked)}
                        />
                        <span className="dp-toggle__track" />
                    </label>
                </div>

                {/* Report Interval */}
                <div className="dp-drawer__field">
                    <label className="dp-drawer__label">
                        Report Interval (min) <span className="dp-drawer__required">*</span>
                    </label>
                    <input
                        className={`dp-drawer__input${errors.interval ? ' is-invalid' : ''}`}
                        type="number"
                        min={30}
                        max={1800}
                        step={1}
                        value={interval}
                        onChange={(e) => { setInterval(e.target.value); clearError('interval'); }}
                    />
                    {errors.interval ? <p className="dp-drawer__error">{errors.interval}</p> : null}
                    <p style={{ marginTop: 'var(--space-1, 0.25rem)', fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--color-text-muted, #6b7280)' }}>
                        Minutes between device reports for analysis. 30 minute minimum.
                    </p>
                </div>
            </Drawer>
        </>
    );
};

export default EnvironmentalDrawer;
